'use client';

import Link from '@eliadmin/core/src/components/link';
import { useEffect, useState, type PropsWithChildren } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import type { TDynamicData } from '../../types/dynamic-data';
import type { Service } from '../../types/service';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu({ children, className }: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', className)}>
      {children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  productLinks: TDynamicData[];
  services: Service[];
  title: string;
  href?: string;
  order?: number;
  index?: number;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem({
  productLinks,
  services,
  title,
  href,
  order,
  index,
  className,
}: CategoryMenuItemProps) {
  const LinkComp = href ? (isRelativeHref(getSafeHref(href)) ? Link : 'a') : 'span';
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  const servicesTitles = ['Služby', 'Services', 'Dienstleistungen'];
  const productsTitles = ['PRODUKTY', 'Products', 'Produkte'];

  const items = servicesTitles.includes(title)
    ? services
    : productsTitles.includes(title)
    ? productLinks
    : [];

  const handleMouseEnter = () => {
    if (!items.length) return;
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoverTimeout(
      setTimeout(() => {
        setIsDropdownVisible(true);
      }, 75),
    );
  };

  const handleMouseLeave = () => {
    if (!items.length) return;
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setHoverTimeout(
      setTimeout(() => {
        setIsDropdownVisible(false);
      }, 300),
    );
  };

  const handleLinkClick = () => setIsDropdownVisible(false);

  useEffect(() => {
    return () => {
      if (hoverTimeout) clearTimeout(hoverTimeout);
    };
  }, [hoverTimeout]);

  return (
    <li
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ order }}
      className={cn('relative flex flex-row', 'ea-category-menu-item', className?.root)}
    >
      <LinkComp {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
        href={href ?? ''}
        className={cn(
          'flex items-center justify-center gap-2 text-sm font-medium',
          'ea-category-menu-item__link',
          className?.link,
        )}
      >
        <p className="text-sm font-medium uppercase text-foreground">
          {title}
        </p>
        {index === 0 && (
          <FiChevronDown
            size={18}
            className={cn(isDropdownVisible ? 'rotate-180' : 'rotate-0', 'transition-all')}
          />
        )}
        {index === 1 && (
          <FiChevronDown
            size={18}
            className={cn(isDropdownVisible ? 'rotate-180' : 'rotate-0', 'transition-all')}
          />
        )}
      </LinkComp>

      {isDropdownVisible && items.length > 0 && (
        <div
          className={cn(
            'absolute left-0 top-full z-30 mt-5 flex w-[240px] flex-col items-start gap-4 border-t-[4px] border-primary bg-white p-6 text-left shadow-md',
            'custom-section',
          )}
        >
          {items.map((item, index) => (
            <>
              <Link prefetchOnHover
                prefetch={false}
                target={title === 'PRODUKTY' ? '_blank' : undefined}
                href={
                  servicesTitles.includes(title)
                    ? item.slug
                    : productsTitles.includes(title)
                    ? item?.fields[0]?.value || '#'
                    : '#'
                }
                onClick={handleLinkClick}
                className="text-base text-foreground"
              >
                {item.name}
              </Link>
              {index < items.length - 1 && (
                <Separator orientation="horizontal" className="w-full bg-border" />
              )}
            </>
          ))}
        </div>
      )}
    </li>
  );
}
